import React, { useState, useEffect, useCallback } from "react";
import { Grid, Box, CircularProgress, Typography, makeStyles } from "@material-ui/core";
import { orderBy, keys } from "lodash";
import Album from "../../scenes/Albums/components/Album";
import InfiniteScroll from "react-infinite-scroll-component";

const useStyles = makeStyles((theme) => ({
  loading: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  noAlbums: {
    marginTop: theme.spacing(5),
    textAlign: "center",
  },
  loadingMore: {
    padding: theme.spacing(2),
    textAlign: "center",
  },
  endMessage: {
    margin: theme.spacing(6, 'auto', 3),
    padding: theme.spacing(1.5, 4),
    textAlign: "center",
    maxWidth: 300,
    backgroundColor: theme.palette.grey[200], // Slightly different shade of gray
    borderRadius: 30, // Rounded left and right ends
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  endMessageText: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  }
}));

const AlbumList = ({ 
  albums, 
  loading, 
  filter, 
  sort, 
  getStatusCategory,
  initialPageSize = 12,
  incrementSize = 12
}) => {
  const classes = useStyles();
  const [displayedAlbums, setDisplayedAlbums] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [visibleCount, setVisibleCount] = useState(initialPageSize);

  // Filter albums based on status category
  const filterAlbum = useCallback((album) => {
    const { status } = album;
    if (!album || !status) return false;

    const category = getStatusCategory(status);
    return filter === "all" || category === filter;
  }, [filter, getStatusCategory]);

  // Sort albums based on selected sort option
  const sortAlbum = useCallback((album) => {
    const { title, artists, timestamps } = album;

    if (sort === "date") {
      return timestamps?.uploaded || timestamps?.ingested || 0;
    }

    if (sort === "title") {
      return title.toUpperCase();
    }

    if (sort === "artist") {
      const artista = keys(artists)
        .map((aid) => artists[aid])
        .find((artist) => artist.country !== "");
      return artista?.name?.toUpperCase() || "";
    }
  }, [sort]);

  // Process albums when they change or when filter/sort changes
  useEffect(() => {
    if (!albums || loading) return;

    const filteredAndSorted = orderBy(
      keys(albums).map((upc) => albums[upc]).filter(filterAlbum),
      sortAlbum,
      sort === "date" ? "desc" : "asc"
    );

    setDisplayedAlbums(filteredAndSorted.slice(0, visibleCount));
    setHasMore(filteredAndSorted.length > visibleCount);
  }, [albums, filter, sort, loading, visibleCount, filterAlbum, sortAlbum]);

  // Reset visible count when albums, filter or sort changes
  useEffect(() => {
    setVisibleCount(initialPageSize);
  }, [albums, filter, sort, initialPageSize]);

  // Load more albums
  const loadMore = () => {
    setVisibleCount(prev => prev + incrementSize);
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }

  if (!displayedAlbums.length) {
    return (
      <Typography className={classes.noAlbums}>
        No se encontraron álbums
      </Typography>
    );
  }

  return (
    <InfiniteScroll
      dataLength={displayedAlbums.length}
      next={loadMore}
      hasMore={hasMore}
      loader={
        <Box className={classes.loadingMore}>
          <CircularProgress size={24} />
          <Typography>Cargando más álbums...</Typography>
        </Box>
      }
      endMessage={
        <Box className={classes.endMessage}>
          <Typography variant="body2" className={classes.endMessageText}>No hay más para mostrar</Typography>
        </Box>
      }
    >
      <Grid container spacing={4}>
        {displayedAlbums.map((album) => (
          <Grid item key={`g${album.upc}`} xs={12} sm={6} md={4} lg={3}>
            <Album key={`a${album.upc}`} album={album} />
          </Grid>
        ))}
      </Grid>
    </InfiniteScroll>
  );
};

export default AlbumList;